import {materializeChannelDefaultVisibility} from "./templateSlots.ts";
import {VISIBILITY_CATALOG_FIELD} from "./defaultSceneVisibility.ts";
import {withCurrentBakedBgs} from "../libs/bakedBg.ts";
import {cropsFollowImages} from "../libs/cropFollowsImage.ts";

// ---------------------------------------------------------------------------
// Override cascade — from src/hooks/useDefaultSceneOverrides.js
// ---------------------------------------------------------------------------

export interface OverrideRow {
  channel: string;
  product: string;
  type: string;
  set?: string | null;
  layout?: string | null;
  background?: string | null;
  variant?: string | null;
  scene_hash?: string | null;
  scene_data?: Record<string, unknown> | null;
}

function normalizeProductKey(product: string | null | undefined): string {
  if (!product || product === "ALL") return "*";
  return product;
}

export function makeKey(
  channel: string,
  product: string | null | undefined,
  type: string,
  set: string | null | undefined,
  layout: string | null | undefined,
  background: string | null | undefined,
  variant?: string | null
): string {
  return [
    channel,
    normalizeProductKey(product),
    type,
    set || "",
    layout || "",
    background || "",
    // The brand-treatment axis, appended last so every key written before it
    // existed still reads back the same: a row with no variant keys on "".
    variant || "",
  ].join(":");
}

function buildLookup(overrides: OverrideRow[]): Map<string, OverrideRow> {
  const lookup = new Map<string, OverrideRow>();
  for (const o of overrides || []) {
    const key = makeKey(o.channel, o.product, o.type, o.set, o.layout, o.background, o.variant);
    lookup.set(key, o);
  }
  return lookup;
}

/**
 * Resolve the most specific matching override entry. Returns a full override
 * object (not just scene_hash), or null. Mirrors the client `resolveOverride`.
 */
export function resolveOverride(
  overrides: OverrideRow[],
  channel: string,
  product: string | null | undefined,
  type: string,
  set: string | null | undefined,
  layout: string | null | undefined,
  bg: string | null | undefined,
  variant?: string | null
): OverrideRow | null {
  if (!overrides?.length) return null;

  const lookup = buildLookup(overrides);
  const ch = channel || "";
  const t = type || "";
  const p = product || "";
  const s = set || "";
  const v = variant || "";

  const candidates = [
    layout && v && makeKey(ch, p, t, s, layout, "", v),
    v && makeKey(ch, p, t, s, "", "", v),
    layout && bg && makeKey(ch, p, t, s, layout, bg, ""),
    layout && makeKey(ch, p, t, s, layout, "", ""),
    bg && makeKey(ch, p, t, s, "", bg, ""),
    makeKey(ch, p, t, s, "", "", ""),
    layout && v && makeKey(ch, "*", t, s, layout, "", v),
    v && makeKey(ch, "*", t, s, "", "", v),
    layout && bg && makeKey(ch, "*", t, s, layout, bg, ""),
    layout && makeKey(ch, "*", t, s, layout, "", ""),
    bg && makeKey(ch, "*", t, s, "", bg, ""),
    makeKey(ch, "*", t, s, "", "", ""),
    layout && makeKey(ch, p, t, "", layout, "", ""),
    bg && makeKey(ch, p, t, "", "", bg, ""),
    makeKey(ch, p, t, "", "", "", ""),
    layout && makeKey(ch, "*", t, "", layout, "", ""),
    bg && makeKey(ch, "*", t, "", "", bg, ""),
    makeKey(ch, "*", t, "", "", "", ""),
  ].filter(Boolean) as string[];

  for (const key of candidates) {
    const entry = lookup.get(key);
    if (entry) return entry;
  }

  return null;
}

/**
 * Resolve all matching overrides, ordered least → most specific — the layers
 * that `mergeScenes` collapses into one scene. Mirrors the client
 * `resolveAllOverrides`.
 */
export function resolveAllOverrides(
  overrides: OverrideRow[],
  channel: string,
  product: string | null | undefined,
  type: string,
  set: string | null | undefined,
  layout: string | null | undefined,
  bg: string | null | undefined,
  variant?: string | null
): OverrideRow[] {
  if (!overrides?.length) return [];

  const lookup = buildLookup(overrides);
  const ch = channel || "";
  const t = type || "";
  const p = product || "";
  const s = set || "";
  const v = variant || "";

  const candidates = [
    makeKey(ch, "*", t, "", "", "", ""),
    makeKey(ch, p, t, "", "", "", ""),
    makeKey(ch, "*", t, s, "", "", ""),
    bg && makeKey(ch, "*", t, s, "", bg, ""),
    layout && makeKey(ch, "*", t, s, layout, "", ""),
    // A row for one (layout, background) pair: what a layout changes about
    // one background — the screenshot layout moving the artwork left of the
    // window — merged on top of the background's own row per template key.
    layout && bg && makeKey(ch, "*", t, s, layout, bg, ""),
    // The brand treatment: a row for the whole treatment, then the (layout,
    // variant) cell the design draws as one. Appended after the older layers
    // so nothing saved before the axis existed changes rank.
    v && makeKey(ch, "*", t, s, "", "", v),
    layout && v && makeKey(ch, "*", t, s, layout, "", v),
    makeKey(ch, p, t, s, "", "", ""),
    bg && makeKey(ch, p, t, s, "", bg, ""),
    layout && makeKey(ch, p, t, s, layout, "", ""),
    layout && bg && makeKey(ch, p, t, s, layout, bg, ""),
    v && makeKey(ch, p, t, s, "", "", v),
    layout && v && makeKey(ch, p, t, s, layout, "", v),
  ].filter(Boolean) as string[];

  const result: OverrideRow[] = [];
  const seen = new Set<string>();
  for (const key of candidates) {
    if (seen.has(key)) continue;
    seen.add(key);
    const entry = lookup.get(key);
    if (entry) result.push(entry);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Scene merge — from src/utils/mergeScenes.js
//
// Object fields are shallow-merged per sub-key across layers; scalar and array
// fields use "last defined wins".
// ---------------------------------------------------------------------------

const OBJECT_FIELDS = [
  "titles",
  "subtitles",
  "subtitles2",
  "subtitles3",
  "names",
  "names2",
  "names3",
  "names4",
  "names5",
  "names6",
  "dates",
  "times",
  "badges",
  "links",
  "links2",
  "links3",
  "features",
  "versions",
  "eaps",
  "positions",
  "positions2",
  "productNames",
  "gdnTags",
  "tags",
  "gdnColorVariants",
  "uploadImages",
  "customBgs",
  "customBgUploaded",
  "logos",
  "logoVariants",
  "textColorLocked",
  "buttonColors",
  "bgX",
  "bgY",
  "bgScale",
  "bgRotate",
  "bgFlip",
  "bgOpacities",
  "bgBrightnesses",
  // Baked per-banner backgrounds and what each was cut from (libs/bakedBg.ts).
  "bakedBgs",
  "bakedBgMeta",
  "layouts",
  "variants",
  "bases",
  "uploadX",
  "uploadY",
  "uploadScale",
  "uploadRotate",
  "uploadFlip",
  "logoX",
  "logoY",
  "logoScale",
  "allProductsBgOptions",
  // Pixelation per-template / per-square maps — shallow-merged per sub-key
  // like the other keyed maps above (COART-1465).
  "pixelationCells",
  "pixelationColors",
  "pixelationPalettes",
  "pixelationSizeOverrides",
  "pixelationCoverageOverrides",
  "pixelationDirectionOverrides",
  // A more specific row replaces each template's brush and corner masks.
  "pixelationMasks",
  "pixelationShapes",
];

const SCALAR_FIELDS = [
  "bgOption",
  "layoutOption",
  "variantOption",
  "fontOption",
  "iconOption",
  "isFontToggle",
  "isButtonEnabled",
  "gdnAccentColor",
  "isUppercase",
  "logoHidden",
  "base",
  "bgBrightness",
  "bgBlurValue",
  "bgOpacity",
  "customBg",
  // The banner of the background selector preview (libs/bgPreviewTemplate).
  "bgPreviewTemplate",
  "fontVariation",
  "titleFontSize",
  "generativeGradients",
  "generativeSpeed",
  "generativeCount",
  "generativeStops",
  // Pixelation gate + scalar knobs — "last defined wins". `pixelationEnabled`
  // must be here so an admin's explicit off survives a multi-layer merge
  // (COART-1465).
  "pixelationEnabled",
  "pixelationSource",
  "pixelationSizeCoefficient",
  "pixelationOpacity",
  "pixelationCoverage",
  "pixelationGradual",
  "pixelationOpacityRamp",
  "pixelationBlend",
  "pixelationSeed",
  "pixelationClickAction",
  "pixelationDirection",
  "pixelationTileShape",
  "pixelationTileRoundness",
  "pixelationTileRadius",
  "pixelationShape",
  "pixelationDotPattern",
  "pixelationDotSizeCoefficient",
  "pixelationDotScale",
  "pixelationDotTone",
  "pixelationDotInvert",
  "pixelationDotColor",
  "pixelationDotStructure",
  "pixelationDotBackdrop",
];

const ARRAY_FIELDS = [
  "visible",
  VISIBILITY_CATALOG_FIELD,
  "locked",
  "order",
  "gdnTagOrder",
  // The page's banners (libs/bannerComposition.ts): the most specific row
  // that names a list gives the whole list.
  "hiddenTemplates",
  "extraTemplates",
];

// A field that no list above names still belongs to the scene. The merge keeps
// it with "last defined wins" — it must not drop it. A dropped field loads only
// while one layer matches, and goes away when a second layer appears.
const LISTED_FIELDS = new Set([...OBJECT_FIELDS, ...SCALAR_FIELDS, ...ARRAY_FIELDS]);

// Exported for the parity test, which compares these lists against the client
// originals key-for-key. See the matching note on MERGE_FIELDS in mergeScenes.js.
export const MERGE_FIELDS = Object.freeze({
  OBJECT_FIELDS,
  SCALAR_FIELDS,
  ARRAY_FIELDS,
});

/**
 * Merge scenes ordered least → most specific:
 *   - object fields: shallow merge per sub-key (Object.assign({}, ...layers))
 *   - scalar fields: last defined wins
 *   - array fields: last defined wins (replaced wholesale)
 * Returns null for an empty list, or the single scene unchanged for one.
 */
export function mergeScenes(
  scenes: Array<Record<string, any> | null | undefined>
): Record<string, any> | null {
  const defined = scenes.filter(Boolean) as Record<string, any>[];
  if (defined.length === 0) return null;
  if (defined.length === 1) return defined[0];

  const result: Record<string, any> = {};

  for (const field of OBJECT_FIELDS) {
    const layers = defined.map((s) => s[field]).filter(Boolean);
    if (layers.length > 0) {
      result[field] = Object.assign({}, ...layers);
    }
  }

  for (const field of SCALAR_FIELDS) {
    for (const scene of defined) {
      if (scene[field] !== undefined) {
        result[field] = scene[field];
      }
    }
  }

  for (const field of ARRAY_FIELDS) {
    for (const scene of defined) {
      if (scene[field] !== undefined) {
        result[field] = scene[field];
      }
    }
  }

  for (const scene of defined) {
    for (const [field, value] of Object.entries(scene)) {
      if (!LISTED_FIELDS.has(field) && value !== undefined) {
        result[field] = value;
      }
    }
  }

  return cropsFollowImages(defined, result);
}

// ---------------------------------------------------------------------------
// ALL-products materialization — from src/utils/allProductsScene.js
//
// Applied to each cascade layer BEFORE mergeScenes, exactly as the client does:
// a more specific layer's canonical `titles.main` must win over a less specific
// layer's `all-products-whats-new-title:<CODE>`, which only holds if the scoped
// keys are read onto the canonical ones per layer rather than after the merge.
// ---------------------------------------------------------------------------

const ALL_PRODUCTS_TITLE_KEY_PREFIX = "all-products-whats-new-title:";
const ALL_PRODUCTS_FEATURES_KEY_PREFIX = "all-products-whats-new-features:";
const ALL_PRODUCTS_BG_OPTIONS_FIELD = "allProductsBgOptions";
const WHATS_NEW_TEMPLATE_ID = "WhatsNewPreview";
const WHATS_NEW_BG_FIELDS = ["bgX", "bgY", "bgScale", "bgRotate", "bgFlip"];

/**
 * Read the ALL-products editor's per-product scoped keys onto `productCode`'s
 * canonical keys. No-op for a falsy scene, a falsy product, or "ALL" itself.
 */
export function materializeAllProductsWhatsNewFields(
  scene: Record<string, any> | null | undefined,
  productCode: string | null | undefined
): Record<string, any> | null | undefined {
  if (!scene || !productCode || productCode === "ALL") return scene;

  const titleKey = `${ALL_PRODUCTS_TITLE_KEY_PREFIX}${productCode}`;
  const featureKey = `${ALL_PRODUCTS_FEATURES_KEY_PREFIX}${productCode}`;
  const titleOverride = scene?.titles?.[titleKey];
  const featureOverride = scene?.features?.[featureKey];
  const next: Record<string, any> = {...scene};
  let changed = false;
  if (titleOverride !== undefined) {
    next.titles = {...(scene.titles || {}), main: titleOverride};
    changed = true;
  }
  if (featureOverride !== undefined) {
    next.features = {...(scene.features || {}), main: featureOverride};
    changed = true;
  }
  const bgOptionOverride = scene?.[ALL_PRODUCTS_BG_OPTIONS_FIELD]?.[productCode];
  if (bgOptionOverride !== undefined) {
    next.bgOption = String(bgOptionOverride);
    changed = true;
  }

  const scopedTemplateId = `${WHATS_NEW_TEMPLATE_ID}-${productCode}`;
  for (const field of WHATS_NEW_BG_FIELDS) {
    const fieldValue = scene?.[field];
    const scopedValue = fieldValue?.[scopedTemplateId];
    if (scopedValue !== undefined) {
      next[field] = {
        ...(fieldValue || {}),
        [WHATS_NEW_TEMPLATE_ID]: scopedValue,
      };
      changed = true;
    }
  }

  return changed ? next : scene;
}

/**
 * The full single-key resolution: cascade → per-layer materialization → merge.
 * One function so every consumer (the resolved endpoint, the resolved-all
 * endpoint's per-product loop) collapses the layers identically.
 *
 * `materialize: false` skips the ALL-products scoped→canonical read. Needed by
 * /resolved-all, whose per-product scenes only feed the ALL view's aggregate:
 * the client's aggregate never materialized, and materializing there would let a
 * set-scoped "*" layer's `all-products-whats-new-title:<CODE>` win over a
 * less-specific per-product canonical `titles.main`, changing what the ALL view
 * renders. That single-product-vs-ALL inconsistency is pre-existing legacy
 * behavior; #567 moves the read to the server without altering it.
 */
export function resolveMergedScene(
  rows: OverrideRow[],
  channel: string,
  product: string | null | undefined,
  type: string,
  set: string | null | undefined,
  layout: string | null | undefined,
  background: string | null | undefined,
  options: {materialize?: boolean; variant?: string | null} = {}
): {layers: OverrideRow[]; sceneData: Record<string, any> | null} {
  const {materialize = true, variant = null} = options;
  const layers = resolveAllOverrides(rows, channel, product, type, set, layout, background, variant);
  const scenes = layers.map((o) => {
    const scene = materializeChannelDefaultVisibility(o.scene_data ?? null, channel);
    const materialized = materialize ? materializeAllProductsWhatsNewFields(scene, product) : scene;
    // A row scoped to one background carries its OWN `bgOption`, redundant with
    // its key (every background-level save stamps it, back to the AIR import
    // scripts). `bgOption` is what PICKS which background-scoped row to read —
    // a background-scoped row can only ever hand back presets FOR that
    // background, never a vote on which one to pick. Left in, it wins the
    // scalar "last defined wins" merge over the real product/set-level choice
    // (SCALAR_FIELDS below), because it is always the most specific layer.
    // Surfaced 2026-10-11: reordering IJ's gallery moved a stale
    // background="1" row's bgOption="1" ahead of the new product default.
    return o.background ? omitSelfReferentialBgOption(materialized) : materialized;
  });
  // A baked image is valid only while the merged crop is still the crop it was
  // cut from (libs/bakedBg.ts). A more specific layer, or a writer that does
  // not know baked images, can change the crop. Then the banner uses the crop.
  return {layers, sceneData: withCurrentBakedBgs(mergeScenes(scenes))};
}

function omitSelfReferentialBgOption(
  scene: Record<string, any> | null | undefined
): Record<string, any> | null | undefined {
  if (!scene || !("bgOption" in scene)) return scene;
  const {bgOption, ...rest} = scene;
  return rest;
}
